import type {Evaluator, NodeParameters} from "@cm/graph-v3"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {GeomBuilderContext} from "#template/geometry-processing/geometry-graph"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import type {SceneProperties} from "#template/nodes/render/scene-properties"
import {TemplateNode} from "#template/template-node/template-node"
import {skipped} from "#template/template-node/constants"
import {visitNone} from "#template/template-node/visit-helpers"

export type NonNullableParameters<T> = {[K in keyof T]: NonNullable<T[K]>}

export abstract class GeometryGraphNode<E, Parameters extends NodeParameters = NodeParameters>
    extends TemplateNode<Parameters>
    implements EvaluableTemplateNode<E | null>
{
    protected abstract get evaluatedGeometryDataPrefix(): string

    protected abstract hasValidParameters(parameters: Parameters): parameters is Parameters & NonNullableParameters<Parameters>

    protected abstract setupGeometryData(
        parameters: NonNullableParameters<Parameters>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
        ctx: GeomBuilderContext,
        sceneManager: ISceneManager,
        sceneProperties: SceneProperties | undefined,
    ): {evaluatedGeometryData: BuilderInlet<E | null>; invalidator?: BuilderInlet<null>}

    public override onFilterActive(_context: OnFilterActiveContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<Parameters> {
        if (!this.hasValidParameters(this.parameters)) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<Parameters> {
        const {evaluator, sceneProperties} = context
        const {templateScope, templateContext} = evaluator
        const {sceneManager} = templateContext
        const ctx = sceneManager.getGeometryBuilderContext()

        if (!this.hasValidParameters(this.parameters)) return skipped

        const scope = evaluator.getScope(this)
        const {evaluatedGeometryData, invalidator} = this.setupGeometryData(this.parameters, scope, evaluator, ctx, sceneManager, sceneProperties)

        templateScope.alias(
            invalidator === undefined ? evaluatedGeometryData : scope.phi(evaluatedGeometryData, invalidator),
            `${this.evaluatedGeometryDataPrefix}-${evaluator.getLocalId(this)}`,
        )
        return visitNone(this.parameters)
    }

    public evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<E | null> {
        return scope.unresolvedToNull(evaluator.templateScope.resolve<E | null>(`${this.evaluatedGeometryDataPrefix}-${evaluator.getLocalId(this)}`))
    }
}
